import React from 'react';
import Div from '@/shared/Div';
import Map from '@/components/Map';
import FeatureCard from './FeatureCard';
import classNames from 'classnames';
import { parseMediaFromCMS } from '@/utils/image';
import Container from '../Container';
import CTAs from './CTAs';
import { E2E_SCORLL_CLASSES } from '@/constants/app';
import Link from '@/shared/Link';
import withContentFlags from '@/hoc/withContentFlags';
import useScreen from '@/hooks/useScreen';

type DetailsProps = {
  productName?: string;
  price?: string;
  offer?: string;
  theme?: string;
};

function Details({ productName, price, offer, theme }: DetailsProps) {
  return (
    <Div dataTestId="details">
      <Div
        className={classNames('text-h-2 md:text-hd-2', {
          'text-cti-on-primary': theme == 'light',
          'text-cti-primary': theme == 'dark',
        })}
      >
        {productName}
      </Div>
      <Div
        className={classNames('text-h-4 md:text-hd-3 mt-s2 md:mt-s3', {
          'text-cti-on-primary': theme == 'light',
          'text-cti-primary': theme == 'dark',
        })}
      >
        {price}
      </Div>
      <Div
        className={classNames('text-body-xl md:text-body-md mt-s2 md:mt-s3', {
          'text-cti-on-primary': theme == 'light',
          'text-cti-primary': theme == 'dark',
        })}
      >
        {offer}
      </Div>
    </Div>
  );
}

interface ProductBannerExtraLargeProps extends DetailsProps {
  content: any;
  features: any;
}

function ProductBannerExtraLarge(props: ProductBannerExtraLargeProps) {
  const { content, features } = props;
  const { isDesktop } = useScreen();
  const {
    mobileImage,
    desktopImage,
    theme,
    heading,
    title,
    subTitle,
    description,
    isActive = false,
    href,
  } = content;

  const Wrapper = href ? Link : Div;

  return isActive ? (
    <Wrapper
      href={href}
      dataTestId="product-banner"
      className={classNames(
        'relative block w-full h-[978px] md:h-[1440px] lg:h-[887px] bg-cover bg-top bg-no-repeat',
        {
          'cursor-pointer': href,
        },
      )}
      style={{
        backgroundImage: `url(${parseMediaFromCMS(!isDesktop ? mobileImage : desktopImage).src})`,
      }}
    >
      <Div className={classNames('absolute top-s8  w-full ')}>
        <Container>
          <Div
            dataTestId="title"
            style={{ color: theme == 'light' ? 'white' : 'black' }}
            className={classNames('mt-s3 text-h-1  md:w-110 md:text-desktop-display')}
            dangerouslySetInnerHTML={{ __html: heading }}
          />
        </Container>
      </Div>
      <Div className={classNames('absolute bottom-s8 md:bottom-s9 w-full')}>
        <Container>
          <Details productName={title} price={subTitle} offer={description} theme={theme} />
          <CTAs
            list={content?.ctas || []}
            dataTestId="product-banner"
            className="text-button-normal py-s4 !px-s6 !rounded-3xl"
            mapClassName="flex gap-s3 mt-s5 md:mt-s6"
            theme={theme}
            actAsButton={Boolean(href)}
          />
          <Map
            className={classNames(
              'flex gap-s5 md:gap-s6 overflow-x-auto hide-scrollbar mt-s8 md:mt-s9 leading-none lg:w-full',
              E2E_SCORLL_CLASSES,
            )}
            list={features}
            wrapper="div"
            render={(item, index) => {
              return (
                <FeatureCard
                  key={index}
                  title={item.title}
                  description={item.subTitle}
                  img={item.image}
                />
              );
            }}
          />
        </Container>
      </Div>
    </Wrapper>
  ) : null;
}

export default withContentFlags(React.memo(ProductBannerExtraLarge));
